/* eslint-disable */
import { z } from 'zod';

const stripHtml = (html) => {
  if (!html) return '';
  return html.replace(/<[^>]*>/g, '').trim();
};
const hasMedia = (html = '') => {
  return /<(img|video|iframe|svg|canvas|audio)\b/i.test(html);
};

const richTextRequired = (fieldName) =>
  z.string().refine(
    (val) => {
      if (!val) return false;

      const stripped = stripHtml(val);
      const mediaPresent = hasMedia(val);

      return stripped.length > 0 || mediaPresent;
    },
    { message: `${fieldName} is required` }
  );

// Helper for option value validation
const optionValueSchema = z.object({
  label: z.string(),
  value: z.union([
    richTextRequired('Option value'),     
    z.number().transform(String),          
  ]),
  valuePlain: z.string().optional(),
});

const marksSchema = z.string()  .min(1, 'Marks is required')
  .refine((val) => !isNaN(Number(val)) && Number(val) > 0, {
    message: 'Marks must be a positive number',
  });
const negativeMarksSchema = z.union([
  z.string().refine((val) => val === '' || (!isNaN(Number(val)) && Number(val) >= 0), {
    message: 'Negative marks must be a non-negative number',
  }),
  z.number().min(0, 'Negative marks must be a non-negative number'),
]);
const youtubeUrlSchema = z.string()
  .refine(
    (val) => {
      if (!val || val === '') return true;
      return /^(https?:\/\/)?(www\.)?youtube\.com|youtu\.be/.test(val);
    },
    { message: 'Invalid YouTube URL' }
  );
const baseQuestionSchema = z.object({
  assessmentType: z.array(z.string()).min(1, 'Select at least one category'),
  questionType: z.preprocess(
    (val) => {
      const map = { MCQ: 'MCQ', MCQ_MULTI: 'MCQ_MULTI', NUMERIC: 'NUMERIC', PASSAGE_SINGLE: 'PASSAGE_SINGLE', PASSAGE_MULTI: 'PASSAGE_MULTI' };
      return map[String(val)] ?? val;
    },
    z.enum(['MCQ', 'MCQ_MULTI', 'NUMERIC', 'PASSAGE_SINGLE', 'PASSAGE_MULTI'],
      { errorMap: () => ({ message: 'Invalid question type.' }) }
    )
  ),
  difficultyTag: z.enum(
    ['easy', 'medium', 'hard'],
    { errorMap: () => ({ message: 'Difficulty tag must be: easy, medium, or hard' }) }
  ),
  marks: marksSchema,
  negativeMarks: negativeMarksSchema,
  questionText: richTextRequired('Question text'),
  textExplanation: richTextRequired('Text explanation'),
  youtubeVideoUrl: youtubeUrlSchema,
});

// MCQ Single Select Schema
export const mcqSingleSelectSchema = baseQuestionSchema.extend({
  questionType: z.literal('MCQ'),
  options: z.array(optionValueSchema)
    .min(2, 'At least 2 options are required')
    .max(6, 'Maximum 6 options allowed'),
  correctAnswer: z.string().min(1, 'Correct answer must be selected'),
});

export const JEEMCQSingleSelectSchema = baseQuestionSchema.extend({
  questionType: z.literal('JEEMCQ1'),
  options: z.array(optionValueSchema)
    .min(2, 'At least 2 options are required')
    .max(6, 'Maximum 6 options allowed'),
  correctAnswer: z.string().min(1, 'Correct answer must be selected'),
});

export const JEEMCQMultipleSelectSchema = baseQuestionSchema.extend({
  questionType: z.literal('JEEMCQ2'),
  options: z.array(optionValueSchema)
    .min(2, 'At least 2 options are required')
    .max(6, 'Maximum 6 options allowed'),
  correctAnswer: z.array(z.string()).min(2, 'At least 2 correct answers must be selected'),
});

// MCQ Multiple Select Schema
export const mcqMultipleSelectSchema = baseQuestionSchema.extend({
  questionType: z.literal('MCQ_MULTI'),
  options: z.array(optionValueSchema)
    .min(2, 'At least 2 options are required')
    .max(6, 'Maximum 6 options allowed'),
  correctAnswer: z.array(z.string()).min(2, 'At least 2 correct answers must be selected'),
});



// Numeric Answer Schema
export const numericAnswerSchema = baseQuestionSchema.extend({
  questionType: z.literal('NUMERIC'),
  
  answerType: z.preprocess(
    (val) => (val == null || val === '' ? 'exact' : String(val).toLowerCase()),
    z.enum(['exact', 'range'], {
      errorMap: () => ({ message: 'Answer type must be either exact or range' }),
    })
  ),
  acceptableAnswers: z.preprocess(
    (val) => (val == null ? undefined : String(val)),
    z.string().optional()
  ),
  min_value: z.union([z.string(), z.number()]).optional(),
  max_value: z.union([z.string(), z.number()]).optional(),
}).superRefine((data, ctx) => {
  if (data.answerType === 'exact') {
    if (!data.acceptableAnswers || data.acceptableAnswers.trim() === '') {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'Acceptable answers are required',
        path: ['acceptableAnswers'],
      });
    } else {
      const answers = data.acceptableAnswers.split(',').map(a => a.trim());
      if (!answers.every(ans => ans !== '' && !isNaN(Number(ans)))) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: 'All acceptable answers must be valid numbers',
          path: ['acceptableAnswers'],
        });
      }
    }
  }
  if (data.answerType === 'range') {
    if (data.min_value === undefined || data.min_value === null || data.min_value === '') {
      ctx.addIssue({ code: z.ZodIssueCode.custom, message: 'Min value is required', path: ['min_value'] });
    }
    if (data.max_value === undefined || data.max_value === null || data.max_value === '') {
      ctx.addIssue({ code: z.ZodIssueCode.custom, message: 'Max value is required', path: ['max_value'] });
    }
    if (data.min_value && data.max_value && Number(data.min_value) >= Number(data.max_value)) {
      ctx.addIssue({ code: z.ZodIssueCode.custom, message: 'Max value must be greater than min value', path: ['max_value'] });
    }
  }
});
// Passage Single Question Schema

export const JEEnumericAnswerSchema = baseQuestionSchema.extend({
  questionType: z.literal('JEENUMERIC'),
  
  answerType: z.preprocess(
    (val) => (val == null || val === '' ? 'exact' : String(val).toLowerCase()),
    z.enum(['exact', 'range'], {
      errorMap: () => ({ message: 'Answer type must be either exact or range' }),
    })
  ),
  acceptableAnswers: z.preprocess(
    (val) => (val == null ? undefined : String(val)),
    z.string().optional()
  ),
  min_value: z.union([z.string(), z.number()]).optional(),
  max_value: z.union([z.string(), z.number()]).optional(),
}).superRefine((data, ctx) => {
  if (data.answerType === 'exact') {
    if (!data.acceptableAnswers || data.acceptableAnswers.trim() === '') {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'Acceptable answers are required',
        path: ['acceptableAnswers'],
      });
    } else {
      const answers = data.acceptableAnswers.split(',').map(a => a.trim());
      if (!answers.every(ans => ans !== '' && !isNaN(Number(ans)))) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: 'All acceptable answers must be valid numbers',
          path: ['acceptableAnswers'],
        });
      }
    }
  }
  if (data.answerType === 'range') {
    if (data.min_value === undefined || data.min_value === null || data.min_value === '') {
      ctx.addIssue({ code: z.ZodIssueCode.custom, message: 'Min value is required', path: ['min_value'] });
    }
    if (data.max_value === undefined || data.max_value === null || data.max_value === '') {
      ctx.addIssue({ code: z.ZodIssueCode.custom, message: 'Max value is required', path: ['max_value'] });
    }
    if (data.min_value && data.max_value && Number(data.min_value) >= Number(data.max_value)) {
      ctx.addIssue({ code: z.ZodIssueCode.custom, message: 'Max value must be greater than min value', path: ['max_value'] });
    }
  }
});


export const passageSingleQuestionSchema = baseQuestionSchema.extend({
  questionType: z.literal('PASSAGE_SINGLE'),
  passageText: richTextRequired('Passage text'),
  options: z.array(optionValueSchema)
    .min(2, 'At least 2 options are required')
    .max(6, 'Maximum 6 options allowed'),
  correctAnswer: z.string().min(1, 'Correct answer must be selected'),
});

// Passage Multiple Questions Schema
export const passageMultipleQuestionsSchema = z.object({
  assessmentType: z.array(z.string()).min(1, 'Select at least one category'),
  questionType: z.literal('PASSAGE_MULTI'),
  difficultyTag: z.string().min(1, 'Difficulty tag is required'),
  passageText: richTextRequired('Passage text'),
  questions: z.array(
    z.object({
      questionText: richTextRequired('Question text'),
      options: z.array(optionValueSchema)
        .min(2, 'At least 2 options are required')
        .max(6, 'Maximum 6 options allowed'),
      correctAnswer: z.string().min(1, 'Correct answer must be selected'),
      marks: marksSchema,
      negativeMarks: negativeMarksSchema,
      textExplanation: richTextRequired('Text explanation'),
      youtubeVideoUrl: youtubeUrlSchema,
    })
  ).min(1, 'At least one question is required'),
});


export const ClatMultipleSelectSchema = z.object({
  assessmentType: z.array(z.string()).min(1, 'Select at least one category'),
  questionType: z.literal('CLATPASSAGE'),
  difficultyTag: z.string().min(1, 'Difficulty tag is required'),
  passageText: richTextRequired('Passage text'),
  questions: z.array(
    z.object({
      questionText: richTextRequired('Question text'),
      options: z.array(optionValueSchema)
        .min(2, 'At least 2 options are required')
        .max(6, 'Maximum 6 options allowed'),
      correctAnswer: z.string().min(1, 'Correct answer must be selected'),
      marks: marksSchema,
      negativeMarks: negativeMarksSchema,
      textExplanation: richTextRequired('Text explanation'),
      youtubeVideoUrl: youtubeUrlSchema,
    })
  ).min(1, 'At least one question is required'),
});


export const JEEMultipleSelectSchema = z.object({
  assessmentType: z.array(z.string()).min(1, 'Select at least one category'),
  questionType: z.literal('JEEPASSAGE'),
  difficultyTag: z.string().min(1, 'Difficulty tag is required'),
  passageText: richTextRequired('Passage text'),
  questions: z.array(
    z.object({
      questionText: richTextRequired('Question text'),
      options: z.array(optionValueSchema)
        .min(2, 'At least 2 options are required')
        .max(6, 'Maximum 6 options allowed'),
      correctAnswer: z.string().min(1, 'Correct answer must be selected'),
      marks: marksSchema,
      negativeMarks: negativeMarksSchema,
      textExplanation: richTextRequired('Text explanation'),
      youtubeVideoUrl: youtubeUrlSchema,
    })
  ).min(1, 'At least one question is required'),
});




// export const ClatMultipleSelectSchema = baseQuestionSchema.extend({
//   questionType: z.literal('CLATPASSAGE'),
//   options: z.array(optionValueSchema)
//     .min(2, 'At least 2 options are required')
//     .max(6, 'Maximum 6 options allowed'),
//   correctAnswer: z.array(z.string()).min(2, 'At least 2 correct answers must be selected'),
// });

// export const JEEMultipleSelectSchema = baseQuestionSchema.extend({
//   questionType: z.literal('JEEPASSAGE'),
//   options: z.array(optionValueSchema)
//     .min(2, 'At least 2 options are required')
//     .max(6, 'Maximum 6 options allowed'),
//   correctAnswer: z.array(z.string()).min(2, 'At least 2 correct answers must be selected'),
// });

export const getValidationSchema = (questionType) => {
  const normalized = questionType?.questionTypeCode || questionType;
  console.log(questionType, "questionType");

  switch (normalized) {
    case 'MCQ':
      return mcqSingleSelectSchema;
    case 'JEEMCQ1':
      return JEEMCQSingleSelectSchema;   
    case 'JEEMCQ2':
     return JEEMCQMultipleSelectSchema; 
    case 'MCQ_MULTI':
      return mcqMultipleSelectSchema;
    case 'CLATPASSAGE':
      return ClatMultipleSelectSchema;
    case 'JEEPASSAGE':
      return JEEMultipleSelectSchema;
    case 'NUMERIC':
      return numericAnswerSchema;
    case 'JEENUMERIC':
        return JEEnumericAnswerSchema;
    case 'PASSAGE_SINGLE':
      return passageSingleQuestionSchema;
    case 'PASSAGE_MULTI':
      return passageMultipleQuestionsSchema;
    default:
      return baseQuestionSchema;
  }
};